import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import '../styles/components/legal-pages.scss';

const CookiePage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <PageHeader 
        title="COOKIE POLICY" 
        subtitle="Information about how we use cookies and similar technologies"
      />
      <div className="legal-page-container">
        <div className="legal-content">

        <section>
          <h2>1. Introduction</h2>
          <p>
            Northern Championship ApS ("we", "us", or "our") uses cookies and similar tracking technologies on our website 
            ncseries.gg (the "Service"). This Cookie Policy explains what cookies are, how we use them, and your choices 
            regarding their use.
          </p>
          <p>
            This policy complies with the EU ePrivacy Directive (Cookie Law) and the General Data Protection Regulation (GDPR).
          </p>
        </section>

        <section>
          <h2>2. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your device when you visit a website. They are widely used to 
            make websites work more efficiently and provide information to website owners.
          </p>
          <p>
            Cookies can be "persistent" or "session" cookies:
          </p>
          <ul>
            <li><strong>Persistent cookies:</strong> Remain on your device until deleted manually or automatically</li>
            <li><strong>Session cookies:</strong> Are deleted automatically when you close your browser</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Cookies</h2>
          <p>We use cookies for the following purposes:</p>

          <h3>3.1 Essential Cookies</h3>
          <p>These cookies are necessary for the website to function properly. They include:</p>
          <ul>
            <li><strong>Authentication cookies:</strong> To keep you logged in during your session</li>
            <li><strong>Security cookies:</strong> To detect and prevent security threats</li>
            <li><strong>Load balancing cookies:</strong> To ensure website stability</li>
            <li><strong>User preference cookies:</strong> To remember your language and display preferences</li>
          </ul>

          <h3>3.2 Performance Cookies</h3>
          <p>These cookies help us understand how visitors interact with our website:</p>
          <ul>
            <li><strong>Analytics cookies:</strong> To collect information about page visits, popular content, and user journeys</li>
            <li><strong>Error tracking cookies:</strong> To identify and fix website issues</li>
            <li><strong>Performance monitoring:</strong> To measure website speed and reliability</li>
          </ul>

          <h3>3.3 Functionality Cookies</h3>
          <p>These cookies enable enhanced functionality:</p>
          <ul>
            <li><strong>Preference cookies:</strong> To remember your tournament preferences and settings</li>
            <li><strong>Feature cookies:</strong> To enable features like live match updates</li>
            <li><strong>Localization cookies:</strong> To provide content in your preferred language</li>
          </ul>

          <h3>3.4 Marketing Cookies (Planned)</h3>
          <p>We plan to implement marketing cookies in the future for:</p>
          <ul>
            <li><strong>Remarketing:</strong> To show relevant ads on other platforms</li>
            <li><strong>Social media integration:</strong> To enable social sharing features</li>
            <li><strong>Conversion tracking:</strong> To measure the effectiveness of our marketing campaigns</li>
          </ul>
        </section>

        <section>
          <h2>4. Specific Cookies We Use</h2>
          
          <table className="cookie-table">
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Provider</th>
                <th>Purpose</th>
                <th>Type</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>auth-token</td>
                <td>Northern Championship</td>
                <td>User authentication</td>
                <td>Essential</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>user-preferences</td>
                <td>Northern Championship</td>
                <td>Store user settings</td>
                <td>Functionality</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>consent-status</td>
                <td>Northern Championship</td>
                <td>Cookie consent preference</td>
                <td>Essential</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>session-id</td>
                <td>Northern Championship</td>
                <td>Session management</td>
                <td>Essential</td>
                <td>Session</td>
              </tr>
            </tbody>
          </table>

          <h3>4.1 Third-Party Cookies (Current and Planned)</h3>
          
          <table className="cookie-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Purpose</th>
                <th>Cookie Types</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Steam API</td>
                <td>Authentication and game data</td>
                <td>Essential</td>
                <td>Active</td>
              </tr>
              <tr>
                <td>YouTube/Twitch Embeds</td>
                <td>Video streaming</td>
                <td>Functionality</td>
                <td>Active</td>
              </tr>
              <tr>
                <td>Umami Analytics</td>
                <td>Privacy-focused website analytics (cookie-free)</td>
                <td>None (no cookies)</td>
                <td>Active</td>
              </tr>
              <tr>
                <td>Discord Widget</td>
                <td>Community integration</td>
                <td>Functionality</td>
                <td>Active</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>5. Umami Analytics (Cookie-Free Analytics)</h2>
          <p>
            We use Umami Analytics for privacy-focused website analytics. <strong>Umami does NOT use cookies</strong> for tracking,
            which means:
          </p>
          <ul>
            <li><strong>No cookie consent required:</strong> Since no cookies are used for analytics, you don't need to accept any cookie banner for our analytics tracking</li>
            <li><strong>No personal data collection:</strong> Umami does not collect, store, or process any personally identifiable information</li>
            <li><strong>IP anonymization:</strong> All IP addresses are immediately anonymized and not stored</li>
            <li><strong>No cross-site tracking:</strong> Umami does not track you across different websites</li>
            <li><strong>GDPR compliant:</strong> Fully compliant with European privacy regulations without requiring consent</li>
          </ul>
          <p>
            <strong>What Umami tracks (without cookies):</strong>
          </p>
          <ul>
            <li>Page views and navigation patterns (anonymized)</li>
            <li>Browser and device type (general categories)</li>
            <li>Referral sources</li>
            <li>Geographic region (country level only)</li>
            <li>Custom events for user interactions</li>
          </ul>
          <p>
            All tracking is done using a small JavaScript file that does not store any information on your device.
            The data is aggregated and anonymized on our analytics server, ensuring complete privacy.
          </p>
        </section>

        <section>
          <h2>6. Your Cookie Choices</h2>
          
          <h3>6.1 Cookie Consent</h3>
          <p>
            When you first visit our website, you will be presented with a cookie consent banner. You can choose to:
          </p>
          <ul>
            <li>Accept all cookies</li>
            <li>Accept only essential cookies</li>
            <li>Customize your cookie preferences</li>
          </ul>

          <h3>6.2 Managing Cookies</h3>
          <p>
            You can manage your cookie preferences at any time by:
          </p>
          <ul>
            <li>Clicking the "Cookie Settings" link in the footer</li>
            <li>Accessing cookie preferences in your account settings</li>
            <li>Using your browser's cookie management tools</li>
          </ul>

          <h3>6.3 Browser Controls</h3>
          <p>
            Most browsers allow you to control cookies through their settings:
          </p>
          <ul>
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">Firefox</a></li>
            <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
            <li><a href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noopener noreferrer">Internet Explorer</a></li>
            <li><a href="https://help.opera.com/en/latest/web-preferences/" target="_blank" rel="noopener noreferrer">Opera</a></li>
          </ul>

          <h3>6.4 Consequences of Disabling Cookies</h3>
          <p>
            Please note that disabling certain cookies may impact your experience:
          </p>
          <ul>
            <li>Essential cookies: You may not be able to log in or use core features</li>
            <li>Functionality cookies: Some features may not work as intended</li>
            <li>Performance cookies: We won't be able to improve our services based on usage data</li>
          </ul>
        </section>

        <section>
          <h2>7. Do Not Track</h2>
          <p>
            Some browsers include a "Do Not Track" (DNT) feature. Currently, there is no industry standard for handling 
            DNT requests. Our website does not currently respond to DNT signals, but we respect your cookie choices as 
            described in this policy.
          </p>
        </section>

        <section>
          <h2>8. Local Storage and Similar Technologies</h2>
          <p>
            In addition to cookies, we may use:
          </p>
          <ul>
            <li><strong>Local Storage:</strong> To store user preferences and cache data</li>
            <li><strong>Session Storage:</strong> To maintain data during your browsing session</li>
            <li><strong>IndexedDB:</strong> For offline functionality and performance optimization</li>
          </ul>
          <p>
            These technologies are governed by the same principles as cookies in this policy.
          </p>
        </section>

        <section>
          <h2>9. Updates to This Policy</h2>
          <p>
            We may update this Cookie Policy to reflect changes in our practices or legal requirements. 
            We will notify you of any material changes by:
          </p>
          <ul>
            <li>Updating the "Last updated" date</li>
            <li>Displaying a notice on our website</li>
            <li>Requesting renewed consent where required</li>
          </ul>
        </section>

        <section>
          <h2>10. Contact Us</h2>
          <p>
            If you have questions about our use of cookies or this policy, please contact us at: <a href="mailto:admin@ncseries.gg">admin@ncseries.gg</a>
          </p>
          <p>
            For more information about how we process your personal data, please see our <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </section>

        <section>
          <h2>11. More Information About Cookies</h2>
          <p>
            For more general information about cookies, you can visit:
          </p>
          <ul>
            <li><a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">All About Cookies</a></li>
            <li><a href="https://youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">Your Online Choices (EU)</a></li>
            <li><a href="https://www.datatilsynet.dk/english" target="_blank" rel="noopener noreferrer">Danish Data Protection Agency</a></li>
          </ul>
        </section>
        
        <p className="legal-date">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>
    </div>
  );
};

export default CookiePage;